import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { RequirementShowItem } from '@shared/apis/requirementsApi.types';
import { TransactionType } from '@shared/models/transactions/transactions';
import { RequirementTransactionDrawer } from './RequirementTransactionDrawer';

interface GlobalDrawerContextType {
  openRequirementDrawer: (requirement: RequirementShowItem, transactionType?: TransactionType) => void;
  closeRequirementDrawer: () => void;
}

const GlobalDrawerContext = createContext<GlobalDrawerContextType | null>(null);

export function GlobalDrawerProvider({ children }: { children: ReactNode }) {
  const [drawerState, setDrawerState] = useState<{
    open: boolean;
    requirement: RequirementShowItem | null;
    transactionType: TransactionType | undefined;
  }>({
    open: false,
    requirement: null,
    transactionType: undefined,
  });

  const openRequirementDrawer = (requirement: RequirementShowItem, transactionType?: TransactionType) => {
    setDrawerState({
      open: true,
      requirement,
      transactionType,
    });
  };

  const closeRequirementDrawer = () => {
    setDrawerState((prev) => ({ ...prev, open: false }));
  };

  // Register global function for Angular to call
  useEffect(() => {
    (window as any).openReactRequirementDrawer = (
      requirement: RequirementShowItem,
      transactionType?: TransactionType
    ) => {
      // Let's drawer determine transaction type from requirement tenure
      openRequirementDrawer(requirement, transactionType);
    };

    return () => {
      delete (window as any).openReactRequirementDrawer;
    };
  }, [openRequirementDrawer]);

  return (
    <GlobalDrawerContext.Provider value={{ openRequirementDrawer, closeRequirementDrawer }}>
      {children}
      {drawerState.requirement && (
        <RequirementTransactionDrawer
          requirement={drawerState.requirement}
          transactionType={drawerState.transactionType}
          onClose={closeRequirementDrawer}
        />
      )}
    </GlobalDrawerContext.Provider>
  );
}

export function useGlobalDrawer() {
  const context = useContext(GlobalDrawerContext);
  if (!context) {
    throw new Error('useGlobalDrawer must be used within GlobalDrawerProvider');
  }
  return context;
}
